import { HttpClient } from '@angular/common/http';
import { Component, inject, type OnDestroy, type OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter } from 'rxjs/operators';
import * as CheckPassword from '../../../server/routes/auth/check-password';
import * as ResetPassword from '../../../server/routes/auth/reset-password';
import * as GetPasswordResetToken from '../../../server/routes/auth/reset-password-token';
import { passwordConfig } from '../../../server/services/password-config.service';
import { generatePath } from '../../../server/services/utils.service';

@Component({
  selector: 'app-reset-password',
  templateUrl: './reset-password.component.html',
  styleUrls: ['./reset-password.component.scss'],
  standalone: false,
})
export class ResetPasswordComponent implements OnInit, OnDestroy {
  private activatedRoute = inject(ActivatedRoute);
  private http = inject(HttpClient);

  form: FormGroup<{ password: FormControl<string> }>;
  submitSuccess = false;
  waiting = false;
  formErrorMessage?: string;
  disableSubmit = true;

  accountId: string;
  email: string;
  token: string;
  // #DuplicatePasswordCode - other components have copies of the below code
  // Password related interface features
  passwordSubject: Subject<string>;
  currentFormPassword: string;
  passwordErrorMessage: string;
  passwordGuessesLog10 = 0;
  passwordProgressBarValue = 0;

  errorState?: 'linkError' | 'tokenExpired' | 'tokenNotFound' | 'unknownError';

  subscriptions = new Subscription();

  ngOnInit(): void {
    const routeSubscribe = this.activatedRoute.queryParamMap.subscribe(
      (paramMap) => {
        const accountId = paramMap.get('accountId');
        const token = paramMap.get('token');
        const email = paramMap.get('email');
        const expiresAtString = paramMap.get('expiresAt');

        if (!accountId || !token || !email || !expiresAtString) {
          this.errorState = 'linkError';
          console.error({ accountId, token, email, expiresAtString });
          return;
        }

        if (Date.now() > Number(expiresAtString)) {
          this.errorState = 'tokenExpired';
          console.error({ accountId, token, email, expiresAtString });
          return;
        }

        this.accountId = accountId;
        this.token = token;
        this.email = email;

        const path = generatePath<GetPasswordResetToken.ReqParams>(
          '/api/auth/reset-password-token/:accountId/:token',
          { accountId, token }
        );
        const httpSubscribe = this.http
          .get<GetPasswordResetToken.ResBody>(path)
          .subscribe({
            next: ({ expiresAt }) => {
              this.form = new FormGroup({
                password: new FormControl('', {
                  validators: [
                    Validators.required,
                    Validators.maxLength(passwordConfig.maxLength),
                  ],
                  nonNullable: true,
                }),
              });

              // #DuplicatePasswordCode - other components have copies of the below code
              this.passwordSubject = new Subject<string>();
              const passwordSubscribe = this.passwordSubject
                .pipe(
                  debounceTime(1000),
                  filter((value) => value.length > 0),
                  distinctUntilChanged()
                )
                .subscribe((password) => {
                  this.checkPassword(password);
                });
              this.subscriptions.add(passwordSubscribe);

              const formSubscribe =
                this.form.controls.password.valueChanges.subscribe((value) => {
                  if (typeof value === 'string') this.updatePassword(value);

                  this.disableSubmit = this.form.invalid;
                });
              this.subscriptions.add(formSubscribe);
            },
            error: (error) => {
              if (error.message === 'tokenNotFound') {
                this.errorState = 'tokenNotFound';
              } else if (error.message === 'tokenExpired') {
                this.errorState = 'tokenExpired';
              } else {
                this.errorState = 'unknownError';
              }
            },
          });
        this.subscriptions.add(httpSubscribe);
      }
    );
    this.subscriptions.add(routeSubscribe);
  }

  // #DuplicatePasswordCode - other components have copies of the below code
  updatePassword(password: string): void {
    // If nothing has changed, do nothing
    if (password === this.currentFormPassword) return;
    // Clear errors
    this.form.controls.password.setErrors(null);

    // this.currentFormPassword is used to check that subsequent updates don't show this earlier result
    this.currentFormPassword = password;
    if (password.length === 0) {
      this.form.controls.password.setErrors({ incorrect: true });
      this.passwordErrorMessage = 'Required.';
      return;
    }
    this.passwordSubject.next(password);
  }

  // #DuplicatePasswordCode - other components have copies of the below code
  checkPassword(password: string): void {
    // Only check if the password is still the currentFormPassword
    if (password !== this.currentFormPassword) return;

    const body: CheckPassword.ReqBody = { password };
    const httpSubscribe = this.http
      .post<CheckPassword.ResBody>('/api/auth/check-password', body)
      .subscribe({
        next: (response) => {
          // Ensure that the password has not been updated recently
          if (password !== this.currentFormPassword) return;

          this.passwordProgressBarValue =
            response.guessesLog10 > 12
              ? 100
              : (100 / 12) * response.guessesLog10;
          this.passwordGuessesLog10 = response.guessesLog10;
          if (
            password.length < passwordConfig.minLength ||
            response.guessesLog10 < passwordConfig.minGuessesLog10
          ) {
            this.form.controls.password.setErrors({ incorrect: true });
            this.passwordErrorMessage =
              'Password must be at least ' +
              passwordConfig.minLength +
              ' characters and hard to guess.';
            this.form.controls.password.markAllAsTouched();
          } else {
            this.form.controls.password.setErrors(null);
            this.form.controls.password.markAllAsTouched();
          }
        },
        error: (errorResponse) => {
          this.formErrorMessage = 'There may be a connection issue.';
          console.error(errorResponse);
        },
      });
    this.subscriptions.add(httpSubscribe);
  }

  // #DuplicatePasswordCode - other components have copies of the below code
  updatePasswordProgressColor(passwordGuessesLog10: number): string {
    // we take in passwordGuessesLog10 because it is necessary for angular to
    // observe it update in order for the class to be updated on change
    if (
      passwordGuessesLog10 < passwordConfig.minGuessesLog10 ||
      this.currentFormPassword.length < passwordConfig.minLength
    ) {
      return 'warn';
    } else if (passwordGuessesLog10 < passwordConfig.goodGuessesLog10) {
      return 'accent';
    } else {
      return 'accent';
    }
  }

  submit(formData): void {
    if (this.form.invalid) return;

    // Clear state from previous submissions
    this.formErrorMessage = undefined;

    const body: ResetPassword.ReqBody = {
      password: formData.password,
      token: this.token,
      accountId: this.accountId,
    };

    this.waiting = true;
    const httpSubscribe = this.http
      .post<ResetPassword.ResBody>('/api/auth/reset-password', body)
      .subscribe({
        next: () => {
          this.waiting = false;
          this.submitSuccess = true;
        },
        error: () => {
          this.waiting = false;
          this.formErrorMessage = 'There was a problem submitting the form.';
        },
      });

    this.subscriptions.add(httpSubscribe);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
